import './SchemaPreview.css';

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import DemoSkillField from '../../demo/components/DemoSkillField';
import { SkillFieldValue } from '../../types/athlete';
import {
  FormGroup,
  FormTemplate,
  GENERAL_COMMENTS_GROUP_ID,
  normalizeFormSchema,
} from '../../types/formSchema';
import BackArrowIcon from '../common/BackButton/BackArrowIcon';
import PerCoachCommentsSection
  from '../common/PerCoachCommentsSection/PerCoachCommentsSection';
import SectionLauncherButton
  from '../common/SectionLauncherButton/SectionLauncherButton';

/**
 * Minimum viewport width (px) at which the FormEditor renders the live
 * SchemaPreview pane beside it. Below this, the editor takes the full card
 * and the preview is hidden. Single source of truth — bump this if the
 * 2-column layout needs more or less breathing room.
 */
export const FORM_PREVIEW_MIN_WIDTH = 1300;

export const FORM_PREVIEW_MEDIA_QUERY = `(min-width: ${FORM_PREVIEW_MIN_WIDTH}px)`;

// Preview rows never carry real scoring data, so we share one frozen empty
// value across every DemoSkillField rather than re-parsing `undefined` per row.
const EMPTY_FIELD_VALUE: SkillFieldValue = Object.freeze({});

export interface SchemaPreviewProps {
  draft: FormTemplate;
}

const SchemaPreview: React.FC<SchemaPreviewProps> = ({ draft }) => {
  // FormEditor strips General Comments out of its working state, but the
  // real coach view always includes it. normalizeFormSchema re-injects the
  // canonical GC group so the preview mirrors what coaches will actually see.
  const groups = useMemo(
    () =>
      [...normalizeFormSchema(draft.schema).groups]
        .sort((a, b) => a.order - b.order),
    [draft.schema],
  );

  const [activeGroupId, setActiveGroupId] = useState<string>('');

  // If the active group is removed in the editor, drop back to the launcher.
  useEffect(() => {
    if (activeGroupId && !groups.some((g) => g.id === activeGroupId)) {
      setActiveGroupId('');
    }
  }, [activeGroupId, groups]);

  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null;
  const trimmedName = draft.name.trim() || 'Untitled form';

  return (
    <div className="schema-preview">
      <div className="schema-preview-header">
        <span className="schema-preview-eyebrow">Live preview</span>
        <h3 className="schema-preview-title" title={trimmedName}>
          {trimmedName}
        </h3>
      </div>

      <div className="schema-preview-scroll score-athlete-scroll-region schema-preview-scroll-region">
        <main className="score-athlete-content">
          {activeGroup ? (
            <section className="score-athlete-fields">
              <button
                type="button"
                className="score-athlete-section-header"
                onClick={() => setActiveGroupId('')}
                aria-label="Back to sections"
              >
                <span className="score-athlete-section-header-back-icon" aria-hidden="true">
                  <BackArrowIcon />
                </span>
                <h3 className="score-athlete-section-title">{activeGroup.label}</h3>
              </button>
              {activeGroup.id === GENERAL_COMMENTS_GROUP_ID ? (
                /* Read-only preview: omit onAddComment so the input is hidden. */
                <PerCoachCommentsSection comments={[]} />
              ) : (
                <div className="score-athlete-fields-list">
                  {[...activeGroup.fields]
                    .sort((a, b) => a.order - b.order)
                    .map((field) => (
                      <div key={field.id}>
                        <DemoSkillField
                          field={field}
                          fieldValue={EMPTY_FIELD_VALUE}
                          subjectId="schema-preview"
                          placeholderUploads
                          onChange={() => undefined}
                        />
                      </div>
                    ))}
                </div>
              )}
            </section>
          ) : (
            <section className="score-athlete-section-launcher">
              {groups.map((group: FormGroup) => {
                if (group.id === GENERAL_COMMENTS_GROUP_ID) {
                  return (
                    <SectionLauncherButton
                      key={group.id}
                      label={group.label}
                      scoredCount={0}
                      scorableCount={0}
                      isGeneralComments
                      onClick={() => setActiveGroupId(group.id)}
                    />
                  );
                }
                const scorable = group.fields.filter((f) => f.type !== 'heading');
                return (
                  <SectionLauncherButton
                    key={group.id}
                    label={group.label || 'Untitled section'}
                    scoredCount={0}
                    scorableCount={scorable.length}
                    onClick={() => setActiveGroupId(group.id)}
                  />
                );
              })}
            </section>
          )}
        </main>
      </div>
    </div>
  );
};

export default SchemaPreview;
